import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { InvoiceSyncIndicator } from "components/InvoiceSourceSync/InvoiceSyncIndicator";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { DownloadInvoice } from "components/DownloadInvoice/DownloadInvoice";
import { InvoiceActions } from "components/InvoiceAction/InvoiceAction";
import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { FilterAndSort } from "components/icons/FilterSort";
import { Info } from "components/icons/Info";
import { Contacts as ContactsIcon } from "components/icons/LeftNav/Books/Contacts";
import { PdfFile } from "components/icons/PdfFile";
import { USD } from "constants/currency";
import { DISPLAY_DATE } from "constants/date";
import { pluralize } from "utils/pluralize";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_ICON,
  REVENUE_DATA_SOURCE_OPTIONS,
  REVENUE_DATA_SOURCE_TYPES,
} from "constants/revenueDataSource";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { InvoiceStatus } from "pages/Books/Invoicing/InvoiceFullPageView";
import {
  CellContext,
  ColumnDefTemplate,
  createColumnHelper,
  HeaderContext,
} from "react-table-8.10.7";
import RecurringIcon from "static/images/Recurring.svg";
import { Invoice } from "types/Models/invoice";
import { formatDate } from "utils/formatDate";
import { stopPropagation } from "utils/stopPropagation";
import relativeTime from "dayjs/plugin/relativeTime";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { AIOutlinedIcon } from "components/icons/AIOutlinedIcon";
import { INVOICE_STATUSES } from "constants/invoicing";
import { useModal } from "hooks/useModal";
import { LinkInvoiceToTransactionModal } from "components/InvoiceAction/LinkInvoiceToTransactionModal";
import { CountBadge } from "components/DesignSystem/Tag/CountBadge";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

dayjs.extend(relativeTime);

const createColumn = createColumnHelper<Invoice>();

export const InvoiceSelectColumn = createColumn.display({
  header: ({ table }) => (
    <Checkbox
      checked={table.getIsAllRowsSelected()}
      indeterminate={table.getIsSomeRowsSelected()}
      onChange={table.getToggleAllRowsSelectedHandler()}
      name="select-all-invoice-rows"
      disabled={table.getRowModel().rows.every((row) => !row.getCanSelect())}
    />
  ),
  id: "selection",
  size: 1,
  enableSorting: false,
  cell: ({ row }) => {
    return (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        indeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        key={row.id}
        name={`select-row-${row.id}`}
      />
    );
  },
});

export const InvoiceDateColumn = createColumn.accessor("invoice_date", {
  id: "INVOICE_DATE",
  header: (header) => {
    const handler = header.column.getToggleSortingHandler() || (() => {});

    return (
      <div className="t-group">
        <HeadCellContent
          title="Invoice Date"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: handler,
          }}
        />
      </div>
    );
  },

  size: 16,
  cell: (info) => {
    const value = info.getValue();
    const invoiceRecurring = info.row.original.invoice_settings;

    return (
      <div>
        {value ? (
          <div className="t-flex t-gap-2 t-items-center">
            <ToolTip text={dayjs(value).format("MMM DD, YYYY, hh:mm A")}>
              <span className="t-text-body-sm t-font-medium t-text-text-60">
                {dayjs(value).format(DISPLAY_DATE)}
              </span>
            </ToolTip>
            {invoiceRecurring?.is_recurring_enabled && (
              <ToolTip
                text={`Next invoice on ${formatDate(
                  invoiceRecurring.next_recurring_date
                )}`}
              >
                <span>
                  <img src={RecurringIcon} alt="RecurringIcon" />
                </span>
              </ToolTip>
            )}
          </div>
        ) : (
          "-"
        )}
      </div>
    );
  },
  enableSorting: true,
});

export const DueDateColumn = createColumn.accessor("due_date", {
  id: "DUE_DATE",
  header: (header) => {
    const handler = header.column.getToggleSortingHandler() || (() => {});

    return (
      <div className="t-group">
        <HeadCellContent
          title="Due Date"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: handler,
          }}
        />
      </div>
    );
  },
  size: 20,
  cell: (info) => {
    const value = info.getValue();
    return (
      <div>
        {value ? (
          <div>
            <div className="t-text-body-sm t-font-medium t-text-text-60">
              {dayjs(value).format(DISPLAY_DATE)}
            </div>
            <div className="t-font-medium t-text-text-30 t-text-body-sm sm:t-hidden">
              {dayjs().to(dayjs(value))}
            </div>
          </div>
        ) : (
          "-"
        )}
      </div>
    );
  },
  enableSorting: true,
});

const InvoiceNumber = (
  props: CellContext<Invoice, Invoice["invoice_number"]>
) => {
  const invoiceId = props.row.original.uuid;
  const invoiceNumber = props.getValue();
  const { link } = useConstructInternalLink();
  const { openInNewTab } = (props.table.options.meta || {}) as {
    openInNewTab?: boolean;
  };

  return (
    <>
      <div className="t-flex t-min-w-0 t-max-w-60 t-items-center t-gap-1">
        <ConditionalLink
          to={link(`/books/invoices-and-customers/${invoiceId}`)}
          target={openInNewTab ? "_blank" : undefined}
          className="t-min-w-0"
        >
          <ToolTip text={invoiceNumber}>
            <Button
              customType={invoiceNumber ? "link" : "ghost"}
              className="!t-shrink t-min-w-0 t-max-w-full [&_div]:t-min-w-0"
            >
              <span className="t-truncate">{invoiceNumber || "-"}</span>
            </Button>
          </ToolTip>
        </ConditionalLink>

        <div onClick={stopPropagation}>
          <DownloadInvoice
            trigger={(isLoading, onClick) => (
              <Button
                size="small"
                customType="ghost_icon"
                onClick={onClick}
                isLoading={isLoading}
                disabled={isLoading}
                className="sm:!t-h-7 sm:!t-w-7"
              >
                <span className="t-text-text-30 sm:[&_svg]:!t-size-4">
                  <PdfFile />
                </span>
              </Button>
            )}
            invoice={props.row.original}
          />
        </div>
      </div>
    </>
  );
};

export const InvoiceNumberColumn = createColumn.accessor("invoice_number", {
  header: "Invoice Number",
  size: 16,
  enableSorting: false,
  cell: InvoiceNumber,
});

export const InvoiceTitleColumn = createColumn.accessor("title", {
  header: "Invoice Title",
  size: 20,
  enableSorting: false,
  cell: (info) => {
    return (
      <div className="t-text-body-sm t-text-text-30">
        {info.getValue() || "-"}
      </div>
    );
  },
});

export const SentToColumn = createColumn.accessor("sent_to", {
  id: "SENT_TO",
  header: (header) => {
    const handler = header.column.getToggleSortingHandler() || (() => {});

    return (
      <div className="t-group">
        <HeadCellContent
          title="To"
          sort={{
            nextSortOrder: header.column.getNextSortingOrder(),
            isSorted: header.column.getIsSorted(),
            sortingHandler: handler,
          }}
        />
      </div>
    );
  },
  size: 26,
  cell: (info) => {
    if (!info.getValue()) {
      return <>-</>;
    }

    const emailText =
      info.row.original.customer?.email ||
      info.row.original.customer?.recipient_emails?.join(", ") ||
      "-";

    return (
      <div>
        <div className="t-text-body-sm t-font-medium t-text-text-60">
          {info.getValue() || "-"}
        </div>
        <ToolTip text={emailText}>
          <div className="t-font-medium t-text-text-30 t-text-body-sm t-truncate t-max-w-[200px]">
            {emailText}
          </div>
        </ToolTip>
      </div>
    );
  },
});

export const InvoiceAmountHeader:
  | ColumnDefTemplate<HeaderContext<Invoice, number>>
  | undefined = ({ table: { options } }) => {
  const meta = options.meta as {
    displayCurrency: string;
    setDisplayCurrency: (v: string) => void;
  };

  return (
    <p className="t-text-right t-m-0 t-w-full t-flex t-gap-1.5 t-items-center">
      <span className="t-ml-auto">Amount</span>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <Button customType="ghost_icon" size="xsmall">
            <FilterAndSort />
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={8}
            side="bottom"
            className="t-min-w-48"
            align="end"
          >
            <Dropdown.RadioGroup
              value={meta.displayCurrency}
              onValueChange={meta.setDisplayCurrency}
            >
              <Dropdown.RadioItem value="INVOICE_CURRENCY">
                In invoice's currency
              </Dropdown.RadioItem>

              <Dropdown.RadioItem value="USD">In USD</Dropdown.RadioItem>
            </Dropdown.RadioGroup>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </p>
  );
};

export const InvoiceAmountColumn = createColumn.accessor(
  "payment_record_amount.amount_in_invoice_currency",
  {
    id: "AMOUNT",
    header: InvoiceAmountHeader,
    size: 16,
    enableSorting: false,
    cell: (info) => {
      const meta = info.table.options.meta as {
        displayCurrency: string;
        setDisplayCurrency: (v: string) => void;
        showPendingAmount?: boolean;
      };

      const due = info.row.original.due_balance;
      const usdValue =
        info.row.original.payment_record_amount?.amount_in_base_currency;
      const invoiceAmount = info.row.original.invoice_amount;
      const currentCurrencyValue = info.getValue();
      const pendingAmount = info.row.original.pending_amount;

      const finalDue =
        meta.displayCurrency === USD
          ? Number(usdValue || due || invoiceAmount || 0)
          : Number(currentCurrencyValue || due || invoiceAmount || 0);

      const currency =
        meta.displayCurrency === USD ? USD : info.row.original.currency;

      return (
        <div className="t-flex t-flex-col t-gap-1">
          <div className="t-text-body-sm t-font-medium t-text-right t-flex t-gap-1 t-justify-end t-items-end t-text-text-60">
            <AmountSuperScript amount={finalDue} currencyCode={currency} />
          </div>
          {meta.showPendingAmount &&
            Boolean(pendingAmount) &&
            pendingAmount! && (
              <span className="t-text-text-30 t-text-body-sm t-flex t-justify-end t-items-center t-gap-0.5">
                <AmountSuperScript amount={pendingAmount} /> of{" "}
                <AmountSuperScript amount={finalDue} currencyCode={currency} />{" "}
                <span>yet to be linked</span>
                <ToolTip
                  text={
                    <>
                      <AmountSuperScript amount={finalDue - pendingAmount} />{" "}
                      has already been linked to transaction(s)
                    </>
                  }
                >
                  <span>
                    <Info size="14" />
                  </span>
                </ToolTip>
              </span>
            )}
        </div>
      );
    },
  }
);

export const StatusColumn = createColumn.accessor("status", {
  header: "Status",
  size: 5,
  enableSorting: false,
  cell: (info) => (
    <span className="t-flex t-text-body-sm">
      <InvoiceStatus
        invoiceStatus={info.getValue()}
        pendingAmount={info.row.original.pending_amount}
        invoiceVoidDate={info.row.original.marked_void_at}
        invoiceUnCollectibleDate={info.row.original.marked_uncollectible_at}
        writtenOffAmount={info.row.original.written_off_amount}
        writtenOffDate={info.row.original.written_off_at}
      />
    </span>
  ),
});

export const SourceColumn = createColumn.accessor("source", {
  header: "Source",
  size: 5,
  enableSorting: false,
  cell: (info) => (
    <div className="t-relative t-flex t-items-center t-justify-center">
      <ConditionalToolTip
        condition={
          REVENUE_DATA_SOURCE[info.getValue() as REVENUE_DATA_SOURCE_TYPES] !==
          undefined
        }
        text={
          REVENUE_DATA_SOURCE_OPTIONS[
            info.getValue() as REVENUE_DATA_SOURCE_TYPES
          ]
        }
      >
        <span>
          {info.getValue() === REVENUE_DATA_SOURCE.MANUAL ? (
            <span
              className="t-rounded-full t-bg-white t-overflow-hidden t-min-h-6 t-min-w-6 t-max-h-6 t-max-w-6 t-text-neutral-40 t-border-neutral-0 t-border t-border-solid t-text-center t-p-1"
              role="img"
            >
              <ContactsIcon />
            </span>
          ) : (
            <Avatar
              size="small"
              src={
                REVENUE_DATA_SOURCE_ICON[
                  info.getValue() as REVENUE_DATA_SOURCE_TYPES
                ]
              }
              alt={
                REVENUE_DATA_SOURCE_OPTIONS[
                  info.getValue() as REVENUE_DATA_SOURCE_TYPES
                ] || ""
              }
            />
          )}
        </span>
      </ConditionalToolTip>
      <span className="t-absolute t-left-1/2 t-ml-4 t-flex">
        <InvoiceSyncIndicator invoice={info.row.original} />
      </span>
    </div>
  ),
});

const InvoiceAIColumnCell = (props: { row: { original: Invoice } }) => {
  const status = props.row.original.status;
  const invoiceId = props.row.original.uuid;
  const linkViaModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const transactionsCount = props.row.original.suggested_txns?.length || 0;

  if (transactionsCount === 0 || !hasWritePermission) {
    return null;
  }

  const isRecordPaymentAllowed =
    status === INVOICE_STATUSES.DUE ||
    status === INVOICE_STATUSES.UNCOLLECTIBLE ||
    status === INVOICE_STATUSES.OVERDUE ||
    status === INVOICE_STATUSES.PARTIALLY_PAID;

  const handleAIClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    linkViaModal.open();
  };

  return (
    <>
      <div className="t-flex t-justify-center">
        <ConditionalToolTip
          condition={isRecordPaymentAllowed}
          text={`Inkle AI found ${pluralize(
            transactionsCount,
            "potential payment match",
            "potential payment matches"
          )}`}
        >
          <div className="t-relative t-inline-flex">
            <Button customType="ghost" size="xsmall" onClick={handleAIClick}>
              <AIOutlinedIcon />
            </Button>
            <CountBadge count={transactionsCount} />
          </div>
        </ConditionalToolTip>
      </div>

      <div onClick={stopPropagation}>
        <LinkInvoiceToTransactionModal
          invoiceId={invoiceId}
          isOpen={linkViaModal.isOpen}
          close={linkViaModal.close}
          openSuggestedMatches
        />
      </div>
    </>
  );
};

export const AIColumn = createColumn.display({
  id: "ai",
  header: " ",
  size: 5,
  enableSorting: false,
  cell: (info) => <InvoiceAIColumnCell row={info.row} />,
});

export const ActionsColumn = createColumn.display({
  id: "Actions",
  enableSorting: false,
  header: "",
  size: 5,
  cell: (info) => <InvoiceActions invoice={info.row.original} />,
});
